import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  Calendar,
  User,
  Activity,
  Loader2,
  Briefcase,
  Building2,
  Mail,
  Phone,
  Banknote,
  Hash,
  ShieldCheck,
  Award,
  Contact
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from './ViewEmployee.module.css';
import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import EmployeeDrawer from './EmployeeDrawer';
import NotFound from '../Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/employees/${id}`);
      if (response.data.success) {
        setEmployee(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching employee details:', error);
      toast.error('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Terminating Record?',
      text: `Are you sure you want to delete ${employee.employee_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete record'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/employees/${employee.id}`);
          if (response.data.success) {
            toast.success('Employee record deleted successfully');
            navigate('/employees');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting employee');
        }
      }
    });
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className={styles['view-employee-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Assembling Employee Profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-employee-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Employee Profile</h1>
              <p>Manage professional records, compensation, and organizational placement</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/employees')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Update Profile</span>
              </button>
            </div>
          </div>

          {/* Employee Dashboard Header Card */}
          <div className={styles['employee-dashboard-header']}>
            <div className={styles['header-main']}>
              <div
                className={styles['avatar-large']}
                style={{ background: 'var(--primary-gradient)' }}
              >
                {getInitials(employee.employee_name)}
              </div>
              <div className={styles['employee-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{employee.employee_name}</h1>
                  <span className={`${styles['status-badge']} ${employee.status == 1 ? styles['active'] : styles['inactive']}`}>
                    <ShieldCheck size={14} /> {employee.status == 1 ? 'ACTIVE DUTY' : 'ON LEAVE / INACTIVE'}
                  </span>
                </div>
                <div className={styles['employee-meta']}>
                  <div className={styles['meta-item']}>
                    <Briefcase size={16} />
                    <span>Role: <strong>{employee.designation?.designation_name || 'Staff Member'}</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Building2 size={16} />
                    <span>Dept: <strong>{employee.department?.departement_name || 'General Personnel'}</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Joined: <strong>{employee.joining_date ? new Date(employee.joining_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Pending'}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          {/* <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><Briefcase size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Active Projects</label>
                <h2>04</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Attendance Rate</label>
                <h2>98%</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Calendar size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Leaves Taken</label>
                <h2>02/12</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Security Scan</label>
                <h2>Clear</h2>
              </div>
            </div>
          </div> */}

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Professional Details */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Briefcase size={20} /></div>
                  <h3>Professional Identity</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Official Designation</span>
                      <span className={styles['d-value']}><Award size={16} style={{ color: '#f97316' }} />{employee.designation?.designation_name || 'Not Assigned'}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Allocated Department</span>
                      <span className={styles['d-value']}><Building2 size={16} style={{ color: '#3b82f6' }} />{employee.department?.departement_name || 'General Personnel'}</span>
                    </div>
                  </div>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Employee Identity Code</span>
                      <span className={styles['d-value']} style={{ letterSpacing: '0.05em' }}>{employee.employee_code}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Engagement Date</span>
                      <span className={styles['d-value']}>{employee.joining_date ? new Date(employee.joining_date).toLocaleDateString('en-IN') : 'TBD'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Communication Hub */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Contact size={20} /></div>
                  <h3>Communication Hub</h3>
                </div>
                <div className={styles['contact-list']}>
                  <div className={styles['contact-pill']}>
                    <div className={styles['p-icon']}><Mail size={16} /></div>
                    <div className={styles['p-info']}>
                      <label>PRIMARY EMAIL</label>
                      <span>{employee.email || 'no-email@company.com'}</span>
                    </div>
                  </div>
                  <div className={styles['contact-pill']}>
                    <div className={styles['p-icon']}><Phone size={16} /></div>
                    <div className={styles['p-info']}>
                      <label>MOBILE REFERENCE</label>
                      <span>{employee.phone_number || 'No contact registered'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compensation Details */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Banknote size={20} /></div>
                  <h3>Financial Compensation & Banking</h3>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <label className={styles['d-label']}>Current Monthly CTC (Base)</label>
                  <div className={styles['d-value-large']}>₹{employee.salary ? parseFloat(employee.salary).toLocaleString('en-IN') : '0.00'}</div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                    <div>
                      <label className={styles['d-label']}>BANK ACCOUNT NUMBER</label>
                      <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px', marginTop: '4px' }}>
                        {employee.bank_account_number || 'Not Provided'}
                      </div>
                    </div>
                    <div>
                      <label className={styles['d-label']}>IFSC CODE</label>
                      <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px', marginTop: '4px' }}>
                        {employee.bank_ifsc_code || 'Not Provided'}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ShieldCheck size={16} style={{ color: '#10b981' }} /> Direct bank transfer enabled for this profile.
                  </div>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Account Status Card */}
              <div className={styles['standard-card']} style={{ padding: '0', overflow: 'hidden' }}>
                <div className={`${styles['status-summary']} ${employee.status == 1 ? styles['active'] : styles['inactive']}`}>
                  <div className={styles['summary-icon']}>
                    <Activity size={24} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '800', fontSize: '16px' }}>{employee.status == 1 ? 'Actively Employed' : 'Position Inactive'}</div>
                    <div style={{ fontSize: '11px', opacity: 0.8 }}>Current Legal Status</div>
                  </div>
                </div>
                <div style={{ padding: '0 28px 28px 28px' }}>
                  <div className={styles['info-block']}>
                    <div className={styles['meta-sidebar-item']}>
                      <div className={styles['sidebar-icon']}><Hash size={18} /></div>
                      <div className={styles['sidebar-text']}>
                        <label>EMPLOYEE CODE</label>
                        <span style={{ color: '#f97316' }}>{employee.employee_code}</span>
                      </div>
                    </div>
                    <div className={styles['meta-sidebar-item']}>
                      <div className={styles['sidebar-icon']}><User size={18} /></div>
                      <div className={styles['sidebar-text']}>
                        <label>REGISTERED BY</label>
                        <span>System Administrator</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security/Access Metadata */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={18} /></div>
                  <h3>Access Metadata</h3>
                </div>
                <div className={styles['info-block']}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#64748b' }}>Last Login</span>
                      <span style={{ fontWeight: '700' }}>{new Date().toLocaleDateString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#64748b' }}>Account Type</span>
                      <span style={{ fontWeight: '700' }}>Standard Personnel</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <EmployeeDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        employee={employee}
        onSave={fetchEmployee}
      />
    </div>
  );
};

export default ViewEmployee;
